/**
 * Single source of truth for which languages the EMALINO frontends
 * expose to end users. Imported by both `apps/ui` and `apps/si` to
 * drive language pickers, the i18n loader, and validation.
 *
 * **Language vs. country.** In EMALINO these are two independent
 * concepts:
 *   - `language` — ISO 639-1 code (en, pl, nl) — drives content
 *     translation. This file is the catalog.
 *   - `country`  — ISO 3166-1 code (US, PL, NL) — drives geography,
 *     address formatting, currency, phone codes. See
 *     `apps/ui/src/enums/system.country.ts`.
 * They're combined as `language + '-' + country` only when constructing
 * BCP-47 strings for `Intl.*` formatters; never anywhere else.
 *
 * To enable a language: flip `enabled: true`. To disable: flip to false.
 * Entries are kept (not deleted) so re-enabling is a one-line change and
 * the historical list of considered languages stays visible.
 *
 * When you flip `enabled: true` on a new code, also:
 *   1. Add `apps/ui/src/i18n/<code>/` (and `apps/si/src/i18n/<code>/`)
 *      with translated message bundles.
 *   2. Wire the dynamic-import case in `apps/ui/src/i18n/index.ts` and
 *      `apps/si/src/i18n/index.ts` (the per-app `loadLanguageModule`).
 *   3. Mirror the change in `api/config/locale.php` — the API side
 *      stays on Laravel's "locale" idiom but the catalog values are the
 *      same ISO 639-1 codes.
 *   4. Add the code to `LOCALES` in `apps/ui/src/seo/publicPages.ts` and give
 *      every `PUBLIC_PAGES` entry a slug for it. That file drives the
 *      locale-prefixed marketing tree (`/en /de /pl /nl`), and the router
 *      guard syncs the active language FROM those URLs — so a language
 *      enabled here but missing there is reset to English on every marketing
 *      page a visitor opens. Pinned by
 *      `apps/ui/tests/unit/seo/publicPages.spec.ts`.
 *
 * Missing message bundles fall back to `en` at runtime — switching to
 * an `enabled: true` language that has no bundle yet won't crash, but
 * every key will render in English. That's intentional: the switch is
 * the commitment to translate; the translations land afterward.
 */

export interface LanguageConfig {
  /** ISO 639-1 code (e.g. 'en', 'pl', 'nl'). */
  code: string;
  /** English name shown in dev tools, admin UIs, logs. */
  name: string;
  /** Native name shown to end users in language pickers. */
  nativeName: string;
  /** Whether end users can select this language. */
  enabled: boolean;
  /** Right-to-left script (Arabic etc.). Defaults to false. */
  rtl?: boolean;
}

export const LANGUAGES: readonly LanguageConfig[] = [
  // -- Enabled (translations shipped or in progress) --
  { code: 'en', name: 'English', nativeName: 'English', enabled: true },
  { code: 'de', name: 'German', nativeName: 'Deutsch', enabled: true },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', enabled: true },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', enabled: true },

  // -- Disabled (kept for re-enabling later; flip `enabled: true` after adding a bundle) --
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', enabled: false, rtl: true },
  { code: 'bg', name: 'Bulgarian', nativeName: 'Български', enabled: false },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', enabled: false },
  { code: 'da', name: 'Danish', nativeName: 'Dansk', enabled: false },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', enabled: false },
  { code: 'es', name: 'Spanish', nativeName: 'Español', enabled: false },
  { code: 'et', name: 'Estonian', nativeName: 'Eesti', enabled: false },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi', enabled: false },
  { code: 'fr', name: 'French', nativeName: 'Français', enabled: false },
  { code: 'ga', name: 'Irish', nativeName: 'Gaeilge', enabled: false },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', enabled: false },
  { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski', enabled: false },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar', enabled: false },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', enabled: false },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', enabled: false },
  { code: 'ko', name: 'Korean', nativeName: '한국어', enabled: false },
  { code: 'lt', name: 'Lithuanian', nativeName: 'Lietuvių', enabled: false },
  { code: 'lv', name: 'Latvian', nativeName: 'Latviešu', enabled: false },
  { code: 'mt', name: 'Maltese', nativeName: 'Malti', enabled: false },
  { code: 'nb', name: 'Norwegian Bokmål', nativeName: 'Norsk bokmål', enabled: false },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', enabled: false },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', enabled: false },
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina', enabled: false },
  { code: 'sl', name: 'Slovenian', nativeName: 'Slovenščina', enabled: false },
  { code: 'sr', name: 'Serbian', nativeName: 'Српски', enabled: false },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', enabled: false },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', enabled: false },
  { code: 'zh', name: 'Chinese', nativeName: '中文', enabled: false },
];

export const DEFAULT_LANGUAGE = 'en';
export const FALLBACK_LANGUAGE = 'en';

export function enabledLanguages(): LanguageConfig[] {
  return LANGUAGES.filter((l) => l.enabled);
}

export function enabledLanguageCodes(): string[] {
  return enabledLanguages().map((l) => l.code);
}

export function isLanguageEnabled(code: string): boolean {
  return enabledLanguages().some((l) => l.code === code);
}

export function findLanguage(code: string): LanguageConfig | undefined {
  return LANGUAGES.find((l) => l.code === code);
}

export function nativeNameMap(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const l of LANGUAGES) out[l.code] = l.nativeName;
  return out;
}

/**
 * Best enabled language from the browser's own preference list, or null when
 * none of them is enabled (the caller decides the fallback — English, a stored
 * pick, or a device language that outranks the browser).
 *
 * ONE implementation for both apps on purpose. UI and SI each used to decide
 * this for themselves, and a language rule that exists twice is a language rule
 * that drifts: it is exactly how a Polish visitor ended up with an English
 * account. `navigator.languages` is the ordered preference list; `language` is
 * the single-value fallback for the browsers that do not populate it.
 */
export function detectBrowserLanguage(): string | null {
  if (typeof navigator === 'undefined') return null;

  const tags = navigator.languages && navigator.languages.length > 0
    ? navigator.languages
    : (navigator.language ? [navigator.language] : []);

  for (const tag of tags) {
    // Reduce a BCP-47 tag to its ISO 639-1 primary subtag: 'pl-PL' -> 'pl'.
    const code = tag.split('-')[0]?.toLowerCase();
    if (code && isLanguageEnabled(code)) return code;
  }

  return null;
}

export function isRtlLanguage(code: string): boolean {
  return findLanguage(code)?.rtl === true;
}
